import React, { FC } from 'react';
import styled, { Colors } from '../../../theme';

const Container = styled.div<StyleProps>(
  ({ borderWidth, height, borderColor, borderRadius, backgroundColor }) => `
  display: flex;
  align-items: center;
  width: calc(100% - ${borderWidth * 2}px);
  height: calc(${height}rem + ${borderWidth}px * 2);
  border-radius: ${borderRadius}rem;
  border: ${borderWidth}px solid ${borderColor};
  background-color: ${backgroundColor};
`,
);

const Segment: FC<IProps & LocalProps> = ({
  idx,
  value,
  max,
  progressColor,
  borderColor,
  borderWidth,
  borderRadius,
  segmentStyles,
}) => {
  const processed = idx! <= value!;
  const firstEl = idx === 1;
  const lastEl = idx === max;
  const borderSolidStyle = `${borderWidth!}px solid ${borderColor}`;
  const borderTranspatentStyle = `${borderWidth! / 2}px solid transparent`;
  let styles: React.CSSProperties = {
    height: '100%',
    width: '100%',
    backgroundColor: processed ? progressColor : '',
    borderLeft: borderSolidStyle,
    borderRight: borderSolidStyle,
  };
  if (firstEl) {
    styles['borderBottomLeftRadius'] = borderRadius;
    styles['borderTopLeftRadius'] = borderRadius;
    styles['borderLeft'] = borderTranspatentStyle;
  }
  if (lastEl) {
    styles['borderBottomRightRadius'] = borderRadius;
    styles['borderTopRightRadius'] = borderRadius;
    styles['borderRight'] = borderTranspatentStyle;
  }
  styles = { ...styles, ...segmentStyles };
  return (
    <div
      className={`progress-bar-segment progress-bar-segment-${processed ? 'processed' : 'unprocessed'}`}
      style={styles}
    />
  );
};

interface LocalProps {
  idx: number;
}

interface InternalProps {
  max: number;
  value: number;
  ['data-testid']?: string;
}

/* The following values are have internal defaults and typically should not be overridden */
interface StyleProps {
  borderWidth: number; // px
  height: number; // rem
  borderRadius: number; // rem
  borderColor: string; // color value
  progressColor: string;
  backgroundColor: string;
  containerStyles?: React.CSSProperties;
  segmentStyles?: React.CSSProperties;
}

export type IProps = InternalProps & Partial<StyleProps>;

const ProgressBar: React.FC<IProps> = ({
  max,
  borderWidth = 3,
  height = 2,
  borderRadius = 50,
  borderColor = Colors.white,
  progressColor = Colors.indigo,
  backgroundColor = Colors.gray,
  value,
  segmentStyles,
  containerStyles,
  'data-testid': dataTestId,
  ...rest
}) => {
  const styleProps = {
    borderWidth,
    height,
    borderRadius,
    borderColor,
    progressColor,
    backgroundColor,
    segmentStyles,
    containerStyles,
  };
  const internalProps = { max, value, dataTestId };
  return (
    <Container {...styleProps} data-testid={`progress-bar-${dataTestId}`}>
      {Array(max)
        .fill(0)
        .map((_, idx) => {
          const localProps = { idx: idx + 1 };
          return (
            <Segment {...styleProps} {...internalProps} {...localProps} {...rest} key={`progress-bar-segment-${idx}`} />
          );
        })}
    </Container>
  );
};

export default ProgressBar;
